/**
 * Появление новой версии приложения в уже открытой вкладке.
 *
 * Клиент — offline-first: служебный процесс (service worker) отдаёт файлы из
 * кэша, и это правильно. Но проверяет он обновление только при регистрации,
 * то есть при загрузке страницы. Вкладка, открытая утром, до вечера живёт на
 * утренней сборке: выложенное на сервер до человека не доходит, и он видит
 * старое приложение, считая его текущим.
 *
 * Здесь два действия: время от времени спрашивать сервер, нет ли новой
 * версии, и сообщить приложению, когда она встала. Перезагрузку выполняет
 * человек: страница, перезагрузившаяся под руками посреди упражнения, теряет
 * набранное (ТЗ FR-EN-69).
 */

/** Как часто спрашивать сервер о новой версии, мс. */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/** Служебный процесс браузера в объёме, который нужен проверке. */
interface ServiceWorkerLike {
  readonly controller: unknown;
  getRegistration: () => Promise<{ update: () => Promise<unknown> } | undefined>;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

/** Документ в объёме, который нужен проверке. */
interface DocumentLike {
  readonly visibilityState?: string;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
}

function serviceWorkerOf(): ServiceWorkerLike | null {
  const container = (globalThis as { navigator?: { serviceWorker?: ServiceWorkerLike } }).navigator
    ?.serviceWorker;
  return container ?? null;
}

function documentOf(): DocumentLike | undefined {
  return (globalThis as { document?: DocumentLike }).document;
}

/**
 * Следит за появлением новой версии.
 *
 * @param onReady вызывается один раз, когда новая версия готова к применению;
 * @param intervalMs как часто спрашивать сервер;
 * @returns отписка.
 */
export function watchAppUpdate(
  onReady: () => void,
  intervalMs: number = UPDATE_CHECK_INTERVAL_MS,
): () => void {
  const worker = serviceWorkerOf();
  if (worker === null) return () => {};

  // Первая в жизни вкладки смена управляющего процесса — это не обновление, а
  // установка: до неё страницу никто не обслуживал. Сообщать о ней нечего.
  const hadController = worker.controller !== null && worker.controller !== undefined;

  const check = (): void => {
    void worker
      .getRegistration()
      .then((registration) => registration?.update())
      .catch(() => {
        // Сеть недоступна — проверим в следующий раз. Offline-first клиент от
        // неудачной проверки обновления страдать не должен.
      });
  };

  const onChange = (): void => {
    if (hadController) onReady();
  };

  // Возвращение к вкладке — самый частый момент, когда человек готов увидеть
  // предложение обновиться, и самый дешёвый повод проверить.
  const onVisible = (): void => {
    if (documentOf()?.visibilityState === 'visible') check();
  };

  worker.addEventListener('controllerchange', onChange);
  documentOf()?.addEventListener?.('visibilitychange', onVisible);
  const timer = setInterval(check, intervalMs);
  check();

  return () => {
    clearInterval(timer);
    worker.removeEventListener('controllerchange', onChange);
    documentOf()?.removeEventListener?.('visibilitychange', onVisible);
  };
}

/** Применяет новую версию: страница перезагружается по решению человека. */
export function applyAppUpdate(): void {
  (globalThis as { location?: { reload: () => void } }).location?.reload();
}
